export type TruevanceWorkspaceKey =
  | "hub"
  | "consulting-pipeline"
  | "assessment-framework"
  | "client-engagement-dashboard"
  | "service-categories"
  | "platform-architecture";

export const truevanceWorkspaceLinks: Array<{ label: string; to: string; description: string }> = [
  {
    label: "Truevance Connect Business Center",
    to: "/admin/truevance-connect",
    description: "Executive overview of the consulting operating layer.",
  },
  {
    label: "Sales Center",
    to: "/admin/truevance-sales-center",
    description: "Lead intake, discovery calls, opportunities, and follow-up management.",
  },
  {
    label: "Proposal Center",
    to: "/admin/truevance-proposal-center",
    description: "Proposal records, line items, approval states, acceptance, and client conversion.",
  },
  {
    label: "Consulting Pipeline",
    to: "/admin/consulting-pipeline",
    description: "Track discovery through ongoing support.",
  },
  {
    label: "Assessment Framework",
    to: "/admin/assessment-framework",
    description: "Capture challenges, opportunities, and recommendations.",
  },
  {
    label: "Client Engagement Dashboard",
    to: "/admin/client-engagement-dashboard",
    description: "See active consulting clients and project statuses.",
  },
  {
    label: "Service Categories",
    to: "/admin/service-categories",
    description: "Organize consulting and digital solution offerings.",
  },
  {
    label: "Platform Architecture",
    to: "/admin/platform-architecture",
    description: "Document reusable components for ClientFlow Studio.",
  },
  {
    label: "Opportunity Management",
    to: "/admin/truevance-opportunities",
    description: "Persistent consulting pipeline records and next actions.",
  },
  {
    label: "Assessment Records",
    to: "/admin/truevance-assessments",
    description: "Persistent business assessment records and recommendations.",
  },
  {
    label: "Consulting Recommendations",
    to: "/admin/truevance-recommendations",
    description: "Solution recommendations linked to assessments and opportunities.",
  },
  {
    label: "Implementation Roadmaps",
    to: "/admin/implementation-roadmaps",
    description: "Quick wins through long-term initiatives for client delivery.",
  },
  {
    label: "Consulting Projects",
    to: "/admin/truevance-projects",
    description: "Persistent project delivery records and completion tracking.",
  },
  {
    label: "Tasks Center",
    to: "/admin/truevance-tasks",
    description: "Brand-scoped task tracking for client and project work.",
  },
  {
    label: "Document Center",
    to: "/admin/truevance-document-center",
    description: "Client and project folders, document records, and upload placeholders.",
  },
  {
    label: "Roles & Permissions",
    to: "/admin/truevance-roles-center",
    description: "Role definitions, permission mappings, and user assignment foundation.",
  },
  {
    label: "Communication Center",
    to: "/admin/truevance-communication-center",
    description: "Client communication history, internal notes, follow-ups, and reminders.",
  },
  {
    label: "Analytics Center",
    to: "/admin/truevance-analytics-center",
    description: "Lead, opportunity, client, and project metrics for the consulting operation.",
  },
];

export const consultingPipelineStages = [
  {
    label: "Discovery Consultation",
    summary: "Clarify goals, stakeholders, and the business problem worth solving.",
    signal: "Inquiry or referral accepted",
    next: "Move into business assessment",
  },
  {
    label: "Business Assessment",
    summary: "Review processes, current systems, and operational pain points.",
    signal: "Assessment notes captured",
    next: "Shape strategy session agenda",
  },
  {
    label: "Strategy Session",
    summary: "Align on priorities, outcomes, scope, and immediate opportunities.",
    signal: "Stakeholder alignment reached",
    next: "Draft solution plan",
  },
  {
    label: "Solution Planning",
    summary: "Map workflows, automation opportunities, and implementation phases.",
    signal: "Scope and dependencies documented",
    next: "Prepare proposal",
  },
  {
    label: "Proposal Development",
    summary: "Package recommendations into a client-ready consulting plan.",
    signal: "Proposal ready for review",
    next: "Send proposal for approval",
  },
  {
    label: "Implementation",
    summary: "Build the approved systems, processes, dashboards, or portals.",
    signal: "Workstream launched",
    next: "Monitor adoption and adoption gaps",
  },
  {
    label: "Optimization",
    summary: "Refine operations, reports, and automations after launch.",
    signal: "Performance feedback received",
    next: "Schedule ongoing support",
  },
  {
    label: "Ongoing Support",
    summary: "Provide continued advisory support, maintenance, and strategic iteration.",
    signal: "Client success cadence established",
    next: "Create expansion opportunities",
  },
];

export const assessmentFramework = [
  {
    label: "Business Challenges",
    items: ["Manual work", "Disconnected systems", "Slow response times", "Inconsistent client experience"],
  },
  {
    label: "Opportunities",
    items: ["Automation wins", "Service clarity", "Improved reporting", "Faster delivery"],
  },
  {
    label: "Current Processes",
    items: ["Inquiry handling", "Follow-up cadence", "Project handoff", "Status updates"],
  },
  {
    label: "Existing Systems",
    items: ["Website", "CRM", "Forms", "Dashboards", "Payment tools"],
  },
  {
    label: "Bottlenecks",
    items: ["Lost leads", "Manual reminders", "No status visibility", "Scattered data"],
  },
  {
    label: "Recommendations",
    items: ["Centralize records", "Automate handoffs", "Clarify ownership", "Standardize reporting"],
  },
  {
    label: "Suggested Solutions",
    items: ["Workflow engine", "Executive dashboard", "Client portal", "Reusable templates"],
  },
];

export const serviceCategories = [
  {
    label: "Business Consulting",
    description: "Operational planning, growth strategy, and advisory work for leaders and teams.",
  },
  {
    label: "Client Experience Consulting",
    description: "Design better customer journeys, onboarding, communication, and retention systems.",
  },
  {
    label: "Digital Solutions Consulting",
    description: "Define the right tools, portals, automations, and reporting needed to support growth.",
  },
  {
    label: "Website & Portal Projects",
    description: "Plan, design, and coordinate websites, portals, and private client experiences.",
  },
  {
    label: "Automation Projects",
    description: "Build reminders, status transitions, handoffs, and repeatable workflow logic.",
  },
  {
    label: "Dashboard Projects",
    description: "Create executive views, operational reporting, and activity visibility.",
  },
  {
    label: "Business Systems Projects",
    description: "Bring proposals, agreements, payments, tasks, and support into one system.",
  },
];

export const engagementMetrics = [
  { label: "Active Consulting Clients", value: 7, note: "Clients currently receiving advisory or implementation support." },
  { label: "Assessments In Progress", value: 4, note: "Discovery and diagnosis work underway." },
  { label: "Strategy Projects", value: 3, note: "Planned solution design and decision support engagements." },
  { label: "Implementations", value: 5, note: "Systems currently being built or launched." },
  { label: "Follow-Ups", value: 8, note: "Open action items and client check-ins." },
  { label: "Opportunities", value: 9, note: "Potential engagements identified from the pipeline." },
];

export const reusablePatterns = [
  {
    label: "Shared workflow engine",
    description: "Status transitions, timeline events, and lifecycle progression that can be white-labeled later.",
    destination: "ClientFlow Studio Core Engine",
  },
  {
    label: "Dashboard shells",
    description: "Command-center pages with KPI cards, filters, tables, and guidance panels.",
    destination: "Shared Workspace Framework",
  },
  {
    label: "Record lifecycle models",
    description: "Lead-to-client, assessment-to-engagement, and proposal-to-implementation record states.",
    destination: "Shared Business Record Model",
  },
  {
    label: "Timeline patterns",
    description: "Chronological event feeds for activity visibility and operational context.",
    destination: "Reusable Activity Timeline",
  },
  {
    label: "Empty-state guidance",
    description: "Helpful copy that keeps users oriented when a workspace has no live data yet.",
    destination: "Template UX Language",
  },
];

export const consultingOpportunityStageOptions = [
  "New Inquiry",
  "Discovery Scheduled",
  "Discovery Complete",
  "Assessment In Progress",
  "Recommendations Presented",
  "Proposal Sent",
  "Proposal Accepted",
  "Implementation Active",
  "Completed",
  "Lost Opportunity",
] as const;

export type ConsultingOpportunityStage = (typeof consultingOpportunityStageOptions)[number];

export const consultingOpportunityStatusOptions = ["Active", "Won", "Lost", "On Hold", "Archived"] as const;
export type ConsultingOpportunityStatus = (typeof consultingOpportunityStatusOptions)[number];

export const assessmentStatusOptions = ["Planned", "In Progress", "Recommendations Presented", "Complete", "Follow-Up Needed", "Archived"] as const;
export type AssessmentStatus = (typeof assessmentStatusOptions)[number];

export const assessmentPriorityOptions = ["Low", "Medium", "High", "Critical"] as const;
export type AssessmentPriority = (typeof assessmentPriorityOptions)[number];

export const assessmentCategoryOptions = [
  "Business Operations",
  "Customer Experience",
  "Digital Presence",
  "Website Performance",
  "Process Efficiency",
  "Automation Readiness",
] as const;
export type AssessmentCategory = (typeof assessmentCategoryOptions)[number];

export const recommendationStatusOptions = ["Draft", "In Progress", "Delivered", "Archived"] as const;
export type RecommendationStatus = (typeof recommendationStatusOptions)[number];

export const roadmapStatusOptions = ["Planned", "Active", "On Hold", "Complete", "Archived"] as const;
export type RoadmapStatus = (typeof roadmapStatusOptions)[number];

export const roadmapHorizonOptions = ["Quick Wins", "Short-Term Projects", "Mid-Term Projects", "Long-Term Initiatives"] as const;
export type RoadmapHorizon = (typeof roadmapHorizonOptions)[number];

export const projectPhaseOptions = ["Discovery", "Assessment", "Recommendations", "Proposal", "Implementation", "Optimization", "Completed"] as const;
export type ProjectPhase = (typeof projectPhaseOptions)[number];

export const projectStatusOptions = ["Active", "On Hold", "Complete", "Archived"] as const;
export type ProjectStatus = (typeof projectStatusOptions)[number];

export interface TruevanceTimelineEvent {
  id: string;
  date: string;
  title: string;
  detail: string;
}

export interface ConsultingOpportunityRecord {
  id: string;
  opportunityId: string;
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  industry: string;
  leadSource: string;
  opportunityType: string;
  estimatedRevenue: number;
  probability: number;
  currentStage: ConsultingOpportunityStage;
  assignedConsultant: string;
  nextAction: string;
  followUpDate: string;
  notes: string;
  status: ConsultingOpportunityStatus;
  timelineEvents: TruevanceTimelineEvent[];
  createdAt: string;
  updatedAt: string;
}

export interface BusinessAssessmentRecord {
  id: string;
  assessmentId: string;
  client: string;
  assessmentName: string;
  assessmentCategory: AssessmentCategory;
  assessmentQuestions: string[];
  assessmentScores: Array<{ area: string; score: number }>;
  assessmentDate: string;
  businessChallenges: string;
  operationalChallenges: string;
  customerExperienceChallenges: string;
  technologyChallenges: string;
  recommendations: string;
  priorityLevel: AssessmentPriority;
  estimatedImpact: string;
  assignedConsultant: string;
  followUpActions: string;
  status: AssessmentStatus;
  timelineEvents: TruevanceTimelineEvent[];
  createdAt: string;
  updatedAt: string;
}

export interface ConsultingRecommendationRecord {
  id: string;
  recommendationId: string;
  assessmentId: string;
  assessmentName: string;
  client: string;
  opportunityIdentified: string;
  impactLevel: string;
  priorityLevel: AssessmentPriority;
  estimatedEffort: string;
  suggestedSolution: string;
  serviceCategoryAlignment: string;
  notes: string;
  status: RecommendationStatus;
  timelineEvents: TruevanceTimelineEvent[];
  createdAt: string;
  updatedAt: string;
}

export interface ImplementationRoadmapRecord {
  id: string;
  roadmapId: string;
  roadmapHorizon: RoadmapHorizon;
  client: string;
  title: string;
  priorityLevel: AssessmentPriority;
  owner: string;
  dueDate: string;
  notes: string;
  linkedAssessmentId: string;
  linkedAssessmentName: string;
  linkedOpportunityId: string;
  linkedOpportunityName: string;
  status: RoadmapStatus;
  timelineEvents: TruevanceTimelineEvent[];
  createdAt: string;
  updatedAt: string;
}

export interface ConsultingProjectRecord {
  id: string;
  projectId: string;
  client: string;
  startDate: string;
  currentPhase: ProjectPhase;
  completionPercent: number;
  revenue: number;
  assignedTeam: string;
  notes: string;
  status: ProjectStatus;
  timelineEvents: TruevanceTimelineEvent[];
  createdAt: string;
  updatedAt: string;
}

export interface TruevanceStore {
  opportunities: ConsultingOpportunityRecord[];
  assessments: BusinessAssessmentRecord[];
  recommendations: ConsultingRecommendationRecord[];
  roadmaps: ImplementationRoadmapRecord[];
  projects: ConsultingProjectRecord[];
}

export interface TruevanceServiceLibraryItem {
  id: string;
  serviceCode: string;
  serviceName: string;
  serviceCategory: string;
  serviceGroup: string;
  description: string;
  status: "active" | "inactive";
  sortOrder: number;
}

export interface TruevanceWorkspacePayload {
  store: TruevanceStore;
  serviceLibrary: TruevanceServiceLibraryItem[];
}

export const truevanceOperationalLinks = [
  {
    label: "Sales Center",
    to: "/admin/truevance-sales-center",
    description: "Lead intake, discovery calls, opportunities, and follow-up management.",
  },
  {
    label: "Opportunity Management",
    to: "/admin/truevance-opportunities",
    description: "Persistent consulting pipeline records and next actions.",
  },
  {
    label: "Assessment Records",
    to: "/admin/truevance-assessments",
    description: "Persistent business assessment records and recommendations.",
  },
  {
    label: "Consulting Recommendations",
    to: "/admin/truevance-recommendations",
    description: "Solution recommendations linked to assessments and opportunities.",
  },
  {
    label: "Implementation Roadmaps",
    to: "/admin/implementation-roadmaps",
    description: "Quick wins through long-term initiatives for client delivery.",
  },
  {
    label: "Consulting Projects",
    to: "/admin/truevance-projects",
    description: "Persistent project delivery records and completion tracking.",
  },
  {
    label: "Tasks Center",
    to: "/admin/truevance-tasks",
    description: "Track business actions, follow-ups, and internal work items.",
  },
  {
    label: "Document Center",
    to: "/admin/truevance-document-center",
    description: "Client and project folder records plus placeholder document uploads.",
  },
  {
    label: "Roles & Permissions",
    to: "/admin/truevance-roles-center",
    description: "Role definitions, permission mapping, and assignment controls.",
  },
] as const;

let truevanceStoreCache: TruevanceStore | null = null;

function makeId(prefix: string) {
  const suffix = crypto.randomUUID().slice(0, 8).toUpperCase();
  return `${prefix}-${suffix}`;
}

function nowIso() {
  return new Date().toISOString();
}

function timeline(title: string, detail: string): TruevanceTimelineEvent {
  return {
    id: crypto.randomUUID(),
    date: nowIso(),
    title,
    detail,
  };
}

export function formatDisplayDate(value: string) {
  if (!value) return "Not set";
  const date = new Date(value.includes("T") ? value : `${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(date);
}

export function formatCurrency(value: number | string) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "$0";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(amount);
}

export function formatPercent(value: number | string) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "0%";
  return `${Math.round(amount)}%`;
}

export function initializeTruevanceStore() {
  if (!truevanceStoreCache) {
    truevanceStoreCache = createSeedStore();
  }
  return normalizeStore(truevanceStoreCache);
}

export function loadTruevanceStore(): TruevanceStore {
  if (!truevanceStoreCache) truevanceStoreCache = createSeedStore();
  return normalizeStore(truevanceStoreCache);
}

export function saveTruevanceStore(store: TruevanceStore) {
  truevanceStoreCache = normalizeStore(store);
}

export function createConsultingOpportunity(input: NewConsultingOpportunityInput): ConsultingOpportunityRecord {
  const now = nowIso();
  return {
    id: crypto.randomUUID(),
    opportunityId: makeId("TV-OPP"),
    companyName: input.companyName,
    contactName: input.contactName,
    email: input.email,
    phone: input.phone,
    industry: input.industry,
    leadSource: input.leadSource,
    opportunityType: input.opportunityType,
    estimatedRevenue: input.estimatedRevenue,
    probability: input.probability,
    currentStage: input.currentStage,
    assignedConsultant: input.assignedConsultant,
    nextAction: input.nextAction,
    followUpDate: input.followUpDate,
    notes: input.notes,
    status: input.status,
    timelineEvents: [timeline("Opportunity created", `${input.companyName} added to the consulting pipeline.`)],
    createdAt: now,
    updatedAt: now,
  };
}

export function createBusinessAssessment(input: NewBusinessAssessmentInput): BusinessAssessmentRecord {
  const now = nowIso();
  return {
    id: crypto.randomUUID(),
    assessmentId: makeId("TV-ASM"),
    client: input.client,
    assessmentName: input.assessmentName,
    assessmentCategory: input.assessmentCategory,
    assessmentQuestions: input.assessmentQuestions,
    assessmentScores: input.assessmentScores,
    assessmentDate: input.assessmentDate,
    businessChallenges: input.businessChallenges,
    operationalChallenges: input.operationalChallenges,
    customerExperienceChallenges: input.customerExperienceChallenges,
    technologyChallenges: input.technologyChallenges,
    recommendations: input.recommendations,
    priorityLevel: input.priorityLevel,
    estimatedImpact: input.estimatedImpact,
    assignedConsultant: input.assignedConsultant,
    followUpActions: input.followUpActions,
    status: input.status,
    timelineEvents: [timeline("Assessment created", `${input.client} assessment record created.`)],
    createdAt: now,
    updatedAt: now,
  };
}

export function createConsultingRecommendation(input: NewConsultingRecommendationInput): ConsultingRecommendationRecord {
  const now = nowIso();
  return {
    id: crypto.randomUUID(),
    recommendationId: makeId("TV-REC"),
    assessmentId: input.assessmentId,
    assessmentName: input.assessmentName,
    client: input.client,
    opportunityIdentified: input.opportunityIdentified,
    impactLevel: input.impactLevel,
    priorityLevel: input.priorityLevel,
    estimatedEffort: input.estimatedEffort,
    suggestedSolution: input.suggestedSolution,
    serviceCategoryAlignment: input.serviceCategoryAlignment,
    notes: input.notes,
    status: input.status,
    timelineEvents: [timeline("Recommendation created", `${input.client} recommendation created.`)],
    createdAt: now,
    updatedAt: now,
  };
}

export function createImplementationRoadmap(input: NewImplementationRoadmapInput): ImplementationRoadmapRecord {
  const now = nowIso();
  return {
    id: crypto.randomUUID(),
    roadmapId: makeId("TV-RDM"),
    roadmapHorizon: input.roadmapHorizon,
    client: input.client,
    title: input.title,
    priorityLevel: input.priorityLevel,
    owner: input.owner,
    dueDate: input.dueDate,
    notes: input.notes,
    linkedAssessmentId: input.linkedAssessmentId,
    linkedAssessmentName: input.linkedAssessmentName,
    linkedOpportunityId: input.linkedOpportunityId,
    linkedOpportunityName: input.linkedOpportunityName,
    status: input.status,
    timelineEvents: [timeline("Roadmap created", `${input.client} roadmap created.`)],
    createdAt: now,
    updatedAt: now,
  };
}

export function createConsultingProject(input: NewConsultingProjectInput): ConsultingProjectRecord {
  const now = nowIso();
  return {
    id: crypto.randomUUID(),
    projectId: makeId("TV-PRJ"),
    client: input.client,
    startDate: input.startDate,
    currentPhase: input.currentPhase,
    completionPercent: input.completionPercent,
    revenue: input.revenue,
    assignedTeam: input.assignedTeam,
    notes: input.notes,
    status: input.status,
    timelineEvents: [timeline("Project created", `${input.client} consulting project created.`)],
    createdAt: now,
    updatedAt: now,
  };
}

export function upsertOpportunity(store: TruevanceStore, nextOpportunity: ConsultingOpportunityRecord) {
  const updatedOpportunity = normalizeOpportunity({ ...nextOpportunity, updatedAt: nowIso() });
  const exists = store.opportunities.some((item) => item.id === updatedOpportunity.id);
  const opportunities = exists
    ? store.opportunities.map((item) => (item.id === updatedOpportunity.id ? updatedOpportunity : item))
    : [updatedOpportunity, ...store.opportunities];
  const nextStore = { ...store, opportunities };
  saveTruevanceStore(nextStore);
  return nextStore;
}

export function upsertAssessment(store: TruevanceStore, nextAssessment: BusinessAssessmentRecord) {
  const updatedAssessment = normalizeAssessment({ ...nextAssessment, updatedAt: nowIso() });
  const exists = store.assessments.some((item) => item.id === updatedAssessment.id);
  const assessments = exists
    ? store.assessments.map((item) => (item.id === updatedAssessment.id ? updatedAssessment : item))
    : [updatedAssessment, ...store.assessments];
  const nextStore = { ...store, assessments };
  saveTruevanceStore(nextStore);
  return nextStore;
}

export function upsertRecommendation(store: TruevanceStore, nextRecommendation: ConsultingRecommendationRecord) {
  const updatedRecommendation = normalizeRecommendation({ ...nextRecommendation, updatedAt: nowIso() });
  const exists = store.recommendations.some((item) => item.id === updatedRecommendation.id);
  const recommendations = exists
    ? store.recommendations.map((item) => (item.id === updatedRecommendation.id ? updatedRecommendation : item))
    : [updatedRecommendation, ...store.recommendations];
  const nextStore = { ...store, recommendations };
  saveTruevanceStore(nextStore);
  return nextStore;
}

export function upsertRoadmap(store: TruevanceStore, nextRoadmap: ImplementationRoadmapRecord) {
  const updatedRoadmap = normalizeRoadmap({ ...nextRoadmap, updatedAt: nowIso() });
  const exists = store.roadmaps.some((item) => item.id === updatedRoadmap.id);
  const roadmaps = exists ? store.roadmaps.map((item) => (item.id === updatedRoadmap.id ? updatedRoadmap : item)) : [updatedRoadmap, ...store.roadmaps];
  const nextStore = { ...store, roadmaps };
  saveTruevanceStore(nextStore);
  return nextStore;
}

export function upsertProject(store: TruevanceStore, nextProject: ConsultingProjectRecord) {
  const updatedProject = normalizeProject({ ...nextProject, updatedAt: nowIso() });
  const exists = store.projects.some((item) => item.id === updatedProject.id);
  const projects = exists
    ? store.projects.map((item) => (item.id === updatedProject.id ? updatedProject : item))
    : [updatedProject, ...store.projects];
  const nextStore = { ...store, projects };
  saveTruevanceStore(nextStore);
  return nextStore;
}

export function appendOpportunityEvent(record: ConsultingOpportunityRecord, title: string, detail: string) {
  return normalizeOpportunity({
    ...record,
    timelineEvents: [timeline(title, detail), ...record.timelineEvents],
    updatedAt: nowIso(),
  });
}

export function appendAssessmentEvent(record: BusinessAssessmentRecord, title: string, detail: string) {
  return normalizeAssessment({
    ...record,
    timelineEvents: [timeline(title, detail), ...record.timelineEvents],
    updatedAt: nowIso(),
  });
}

export function appendRecommendationEvent(record: ConsultingRecommendationRecord, title: string, detail: string) {
  return normalizeRecommendation({
    ...record,
    timelineEvents: [timeline(title, detail), ...record.timelineEvents],
    updatedAt: nowIso(),
  });
}

export function appendRoadmapEvent(record: ImplementationRoadmapRecord, title: string, detail: string) {
  return normalizeRoadmap({
    ...record,
    timelineEvents: [timeline(title, detail), ...record.timelineEvents],
    updatedAt: nowIso(),
  });
}

export function appendProjectEvent(record: ConsultingProjectRecord, title: string, detail: string) {
  return normalizeProject({
    ...record,
    timelineEvents: [timeline(title, detail), ...record.timelineEvents],
    updatedAt: nowIso(),
  });
}

export function calculateTruevanceMetrics(store: TruevanceStore) {
  const openOpportunities = store.opportunities.filter((item) => item.status === "Active").length;
  const activeAssessments = store.assessments.filter((item) => item.status !== "Archived" && item.status !== "Complete").length;
  const activeRecommendations = store.recommendations.filter((item) => item.status !== "Archived" && item.status !== "Delivered").length;
  const activeProjects = store.projects.filter((item) => item.status === "Active").length;
  const completedProjects = store.projects.filter((item) => item.status === "Complete").length;
  const consultingRevenue = store.projects.reduce((sum, item) => sum + item.revenue, 0);
  const pipelineValue = store.opportunities.reduce((sum, item) => sum + item.estimatedRevenue * (item.probability / 100), 0);
  const closedOpportunities = store.opportunities.filter((item) => item.status === "Won" || item.status === "Lost").length;
  const wonOpportunities = store.opportunities.filter((item) => item.status === "Won").length;
  const winRate = closedOpportunities ? Math.round((wonOpportunities / closedOpportunities) * 100) : 0;
  const averageProjectValue = store.projects.length ? Math.round(consultingRevenue / store.projects.length) : 0;
  const projectCompletionRate = store.projects.length
    ? Math.round(store.projects.reduce((sum, item) => sum + item.completionPercent, 0) / store.projects.length)
    : 0;
  const implementationProgress = store.roadmaps.length
    ? Math.round(
        store.roadmaps.reduce((sum, item) => sum + (item.status === "Complete" ? 100 : item.status === "Active" ? 65 : item.status === "On Hold" ? 35 : 15), 0) /
          store.roadmaps.length,
      )
    : 0;

  return {
    openOpportunities,
    activeAssessments,
    activeRecommendations,
    activeProjects,
    completedProjects,
    consultingRevenue,
    pipelineValue,
    winRate,
    averageProjectValue,
    projectCompletionRate,
    implementationProgress,
  };
}

export type NewConsultingOpportunityInput = Pick<
  ConsultingOpportunityRecord,
  | "companyName"
  | "contactName"
  | "email"
  | "phone"
  | "industry"
  | "leadSource"
  | "opportunityType"
  | "estimatedRevenue"
  | "probability"
  | "currentStage"
  | "assignedConsultant"
  | "nextAction"
  | "followUpDate"
  | "notes"
  | "status"
>;

export type NewBusinessAssessmentInput = Pick<
  BusinessAssessmentRecord,
  | "client"
  | "assessmentName"
  | "assessmentCategory"
  | "assessmentQuestions"
  | "assessmentScores"
  | "assessmentDate"
  | "businessChallenges"
  | "operationalChallenges"
  | "customerExperienceChallenges"
  | "technologyChallenges"
  | "recommendations"
  | "priorityLevel"
  | "estimatedImpact"
  | "assignedConsultant"
  | "followUpActions"
  | "status"
>;

export type NewConsultingRecommendationInput = Pick<
  ConsultingRecommendationRecord,
  | "assessmentId"
  | "assessmentName"
  | "client"
  | "opportunityIdentified"
  | "impactLevel"
  | "priorityLevel"
  | "estimatedEffort"
  | "suggestedSolution"
  | "serviceCategoryAlignment"
  | "notes"
  | "status"
>;

export type NewImplementationRoadmapInput = Pick<
  ImplementationRoadmapRecord,
  | "roadmapHorizon"
  | "client"
  | "title"
  | "priorityLevel"
  | "owner"
  | "dueDate"
  | "notes"
  | "linkedAssessmentId"
  | "linkedAssessmentName"
  | "linkedOpportunityId"
  | "linkedOpportunityName"
  | "status"
>;

export type NewConsultingProjectInput = Pick<
  ConsultingProjectRecord,
  "client" | "startDate" | "currentPhase" | "completionPercent" | "revenue" | "assignedTeam" | "notes" | "status"
>;

function createSeedStore(): TruevanceStore {
  return {
    opportunities: [
      normalizeOpportunity({
        id: crypto.randomUUID(),
        opportunityId: "TV-OPP-1001",
        companyName: "Northstar Ministries",
        contactName: "Jordan Ellis",
        email: "jordan@northstarministries.org",
        phone: "(555) 018-4400",
        industry: "Nonprofit",
        leadSource: "Referral",
        opportunityType: "Business Consulting",
        estimatedRevenue: 18000,
        probability: 72,
        currentStage: "Assessment In Progress",
        assignedConsultant: "Truevance Connect",
        nextAction: "Review workflow map with leadership",
        followUpDate: "2026-06-14",
        notes: "Needs a centralized client journey and donor communication system.",
        status: "Active",
        timelineEvents: [
          timeline("Opportunity created", "Northstar Ministries entered the Truevance pipeline."),
          timeline("Discovery scheduled", "Consultation booked for the following week."),
        ],
        createdAt: nowIso(),
        updatedAt: nowIso(),
      }),
      normalizeOpportunity({
        id: crypto.randomUUID(),
        opportunityId: "TV-OPP-1002",
        companyName: "Summit Event Group",
        contactName: "Mia Carter",
        email: "mia@summiteventgroup.com",
        phone: "(555) 018-4410",
        industry: "Events",
        leadSource: "Website Inquiry",
        opportunityType: "Digital Solutions Consulting",
        estimatedRevenue: 24000,
        probability: 64,
        currentStage: "Proposal Sent",
        assignedConsultant: "Truevance Connect",
        nextAction: "Follow up on proposal questions",
        followUpDate: "2026-06-12",
        notes: "Interested in dashboard, automation, and internal operations support.",
        status: "Active",
        timelineEvents: [
          timeline("Opportunity created", "Summit Event Group started the consulting journey."),
          timeline("Proposal sent", "Proposal delivered with implementation roadmap."),
        ],
        createdAt: nowIso(),
        updatedAt: nowIso(),
      }),
    ],
    assessments: [
      normalizeAssessment({
        id: crypto.randomUUID(),
        assessmentId: "TV-ASM-1001",
        client: "Northstar Ministries",
        assessmentName: "Northstar Ministries Assessment",
        assessmentCategory: "Business Operations",
        assessmentQuestions: ["Where are the biggest manual steps?", "Which handoffs slow the team down?", "What should be automated first?"],
        assessmentScores: [
          { area: "Business Operations", score: 72 },
          { area: "Customer Experience", score: 68 },
          { area: "Automation Readiness", score: 78 },
        ],
        assessmentDate: "2026-06-10",
        businessChallenges: "Manual follow-up and fragmented lead tracking.",
        operationalChallenges: "Hand-offs between teams are inconsistent.",
        customerExperienceChallenges: "Response times vary based on staff availability.",
        technologyChallenges: "Tools are not connected to a shared operating layer.",
        recommendations: "Centralize workflow status, automate reminders, and unify reporting.",
        priorityLevel: "High",
        estimatedImpact: "Reduce manual work and improve response speed.",
        assignedConsultant: "Truevance Connect",
        followUpActions: "Prepare recommendation deck and implementation estimate.",
        status: "In Progress",
        timelineEvents: [timeline("Assessment created", "Initial operational assessment captured.")],
        createdAt: nowIso(),
        updatedAt: nowIso(),
      }),
    ],
    recommendations: [
      normalizeRecommendation({
        id: crypto.randomUUID(),
        recommendationId: "TV-REC-1001",
        assessmentId: "TV-ASM-1001",
        assessmentName: "Northstar Ministries Assessment",
        client: "Northstar Ministries",
        opportunityIdentified: "Centralized workflow and reporting layer.",
        impactLevel: "High",
        priorityLevel: "High",
        estimatedEffort: "Medium",
        suggestedSolution: "Create a shared operations dashboard and automation backbone.",
        serviceCategoryAlignment: "Digital Solutions",
        notes: "The recommendation is to unify status, activity, and follow-up flows.",
        status: "Delivered",
        timelineEvents: [timeline("Recommendation created", "Solution package drafted from the assessment.")],
        createdAt: nowIso(),
        updatedAt: nowIso(),
      }),
    ],
    roadmaps: [
      normalizeRoadmap({
        id: crypto.randomUUID(),
        roadmapId: "TV-RDM-1001",
        roadmapHorizon: "Quick Wins",
        client: "Northstar Ministries",
        title: "Northstar Quick Wins Roadmap",
        priorityLevel: "High",
        owner: "Truevance Connect",
        dueDate: "2026-06-30",
        notes: "Execute the first wave of dashboard and automation improvements.",
        linkedAssessmentId: "TV-ASM-1001",
        linkedAssessmentName: "Northstar Ministries Assessment",
        linkedOpportunityId: "TV-OPP-1001",
        linkedOpportunityName: "Northstar Ministries",
        status: "Active",
        timelineEvents: [timeline("Roadmap created", "Quick wins roadmap prepared.")],
        createdAt: nowIso(),
        updatedAt: nowIso(),
      }),
    ],
    projects: [
      normalizeProject({
        id: crypto.randomUUID(),
        projectId: "TV-PRJ-1001",
        client: "Summit Event Group",
        startDate: "2026-06-03",
        currentPhase: "Implementation",
        completionPercent: 48,
        revenue: 24000,
        assignedTeam: "Operations + Systems",
        notes: "Building the operations dashboard and client workflow automations.",
        status: "Active",
        timelineEvents: [
          timeline("Project created", "Consulting implementation project opened."),
          timeline("Implementation active", "Workstreams started for dashboards and automations."),
        ],
        createdAt: nowIso(),
        updatedAt: nowIso(),
      }),
    ],
  };
}

function normalizeStore(store: Partial<TruevanceStore>): TruevanceStore {
  return {
    opportunities: Array.isArray(store.opportunities) && store.opportunities.length ? store.opportunities.map(normalizeOpportunity) : createSeedStore().opportunities,
    assessments: Array.isArray(store.assessments) && store.assessments.length ? store.assessments.map(normalizeAssessment) : createSeedStore().assessments,
    recommendations: Array.isArray(store.recommendations) && store.recommendations.length ? store.recommendations.map(normalizeRecommendation) : createSeedStore().recommendations,
    roadmaps: Array.isArray(store.roadmaps) && store.roadmaps.length ? store.roadmaps.map(normalizeRoadmap) : createSeedStore().roadmaps,
    projects: Array.isArray(store.projects) && store.projects.length ? store.projects.map(normalizeProject) : createSeedStore().projects,
  };
}

function normalizeOpportunity(record: ConsultingOpportunityRecord): ConsultingOpportunityRecord {
  return {
    ...record,
    companyName: record.companyName || "Untitled Opportunity",
    contactName: record.contactName || "",
    email: record.email || "",
    phone: record.phone || "",
    industry: record.industry || "General",
    leadSource: record.leadSource || "Manual Entry",
    opportunityType: record.opportunityType || "Business Consulting",
    estimatedRevenue: toNumber(record.estimatedRevenue),
    probability: toNumber(record.probability),
    currentStage: consultingOpportunityStageOptions.includes(record.currentStage) ? record.currentStage : "New Inquiry",
    assignedConsultant: record.assignedConsultant || "Unassigned",
    nextAction: record.nextAction || "Review next step",
    followUpDate: record.followUpDate || "",
    notes: record.notes || "",
    status: consultingOpportunityStatusOptions.includes(record.status) ? record.status : "Active",
    timelineEvents: Array.isArray(record.timelineEvents) && record.timelineEvents.length ? record.timelineEvents.map(normalizeTimelineEvent) : [timeline("Opportunity created", "Record initialized.")],
    createdAt: record.createdAt || nowIso(),
    updatedAt: record.updatedAt || nowIso(),
  };
}

function normalizeAssessment(record: BusinessAssessmentRecord): BusinessAssessmentRecord {
  return {
    ...record,
    client: record.client || "Unnamed Client",
    assessmentName: record.assessmentName || record.client || "Assessment",
    assessmentCategory: assessmentCategoryOptions.includes(record.assessmentCategory) ? record.assessmentCategory : "Business Operations",
    assessmentQuestions: Array.isArray(record.assessmentQuestions) ? record.assessmentQuestions : [],
    assessmentScores: Array.isArray(record.assessmentScores) ? record.assessmentScores : [],
    assessmentDate: record.assessmentDate || "",
    businessChallenges: record.businessChallenges || "",
    operationalChallenges: record.operationalChallenges || "",
    customerExperienceChallenges: record.customerExperienceChallenges || "",
    technologyChallenges: record.technologyChallenges || "",
    recommendations: record.recommendations || "",
    priorityLevel: assessmentPriorityOptions.includes(record.priorityLevel) ? record.priorityLevel : "Medium",
    estimatedImpact: record.estimatedImpact || "",
    assignedConsultant: record.assignedConsultant || "Unassigned",
    followUpActions: record.followUpActions || "",
    status: assessmentStatusOptions.includes(record.status) ? record.status : "Planned",
    timelineEvents: Array.isArray(record.timelineEvents) && record.timelineEvents.length ? record.timelineEvents.map(normalizeTimelineEvent) : [timeline("Assessment created", "Record initialized.")],
    createdAt: record.createdAt || nowIso(),
    updatedAt: record.updatedAt || nowIso(),
  };
}

function normalizeRecommendation(record: ConsultingRecommendationRecord): ConsultingRecommendationRecord {
  return {
    ...record,
    assessmentId: record.assessmentId || "",
    assessmentName: record.assessmentName || "Recommendation",
    client: record.client || "Unnamed Client",
    opportunityIdentified: record.opportunityIdentified || "",
    impactLevel: record.impactLevel || "Medium",
    priorityLevel: assessmentPriorityOptions.includes(record.priorityLevel) ? record.priorityLevel : "Medium",
    estimatedEffort: record.estimatedEffort || "",
    suggestedSolution: record.suggestedSolution || "",
    serviceCategoryAlignment: record.serviceCategoryAlignment || "",
    notes: record.notes || "",
    status: recommendationStatusOptions.includes(record.status) ? record.status : "Draft",
    timelineEvents: Array.isArray(record.timelineEvents) && record.timelineEvents.length ? record.timelineEvents.map(normalizeTimelineEvent) : [timeline("Recommendation created", "Record initialized.")],
    createdAt: record.createdAt || nowIso(),
    updatedAt: record.updatedAt || nowIso(),
  };
}

function normalizeRoadmap(record: ImplementationRoadmapRecord): ImplementationRoadmapRecord {
  return {
    ...record,
    roadmapHorizon: roadmapHorizonOptions.includes(record.roadmapHorizon) ? record.roadmapHorizon : "Quick Wins",
    client: record.client || "Unnamed Client",
    title: record.title || "Implementation Roadmap",
    priorityLevel: assessmentPriorityOptions.includes(record.priorityLevel) ? record.priorityLevel : "Medium",
    owner: record.owner || "Unassigned",
    dueDate: record.dueDate || "",
    notes: record.notes || "",
    linkedAssessmentId: record.linkedAssessmentId || "",
    linkedAssessmentName: record.linkedAssessmentName || "",
    linkedOpportunityId: record.linkedOpportunityId || "",
    linkedOpportunityName: record.linkedOpportunityName || "",
    status: roadmapStatusOptions.includes(record.status) ? record.status : "Planned",
    timelineEvents: Array.isArray(record.timelineEvents) && record.timelineEvents.length ? record.timelineEvents.map(normalizeTimelineEvent) : [timeline("Roadmap created", "Record initialized.")],
    createdAt: record.createdAt || nowIso(),
    updatedAt: record.updatedAt || nowIso(),
  };
}

function normalizeProject(record: ConsultingProjectRecord): ConsultingProjectRecord {
  return {
    ...record,
    client: record.client || "Unnamed Client",
    startDate: record.startDate || "",
    currentPhase: projectPhaseOptions.includes(record.currentPhase) ? record.currentPhase : "Discovery",
    completionPercent: toNumber(record.completionPercent),
    revenue: toNumber(record.revenue),
    assignedTeam: record.assignedTeam || "Unassigned",
    notes: record.notes || "",
    status: projectStatusOptions.includes(record.status) ? record.status : "Active",
    timelineEvents: Array.isArray(record.timelineEvents) && record.timelineEvents.length ? record.timelineEvents.map(normalizeTimelineEvent) : [timeline("Project created", "Record initialized.")],
    createdAt: record.createdAt || nowIso(),
    updatedAt: record.updatedAt || nowIso(),
  };
}

function normalizeTimelineEvent(event: TruevanceTimelineEvent): TruevanceTimelineEvent {
  return {
    id: event.id || crypto.randomUUID(),
    date: event.date || nowIso(),
    title: event.title || "Update",
    detail: event.detail || "",
  };
}

function toNumber(value: unknown) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
}
